<!--
 
 Copyright © 2025-2026 Ping Identity Corporation. All right reserved.
 
 This software may be modified and distributed under the terms
 of the MIT license. See the LICENSE file for details.
 
 -->

<script lang="ts">
  import { afterUpdate, onMount } from 'svelte';

  import T from '$components/_utilities/locale-strings.svelte';
  import KeyIcon from '$components/icons/key-icon.svelte';
  // Import components
  import Alert from '$components/primitives/alert/alert.svelte';
  import Button from '$components/primitives/button/button.svelte';
  import Form from '$components/primitives/form/form.svelte';
  // i18n
  import { interpolate } from '$core/_utilities/i18n.utilities';
  import { encodeCssUrl } from '$core/_utilities/theme.utilities';
  import { styleStore } from '$core/style.store';
  import CallbackMapper from '$journey/_utilities/callback-mapper.svelte';
  import { convertStringToKey } from '$journey/stages/_utilities/step.utilities';
  import { captureLinks } from './_utilities/stage.utilities';

  import type { JourneyStep } from '@forgerock/journey-client/types';

  import type { Maybe } from '$core/interfaces';
  // Types
  import type {
    CallbackMetadata,
    StageFormObject,
    StageJourneyObject,
    StepMetadata,
  } from '$journey/journey.interfaces';

  export let componentStyle: 'app' | 'inline' | 'modal';
  export let form: StageFormObject;
  export let formEl: HTMLFormElement | null = null;
  export let journey: StageJourneyObject;
  export let metadata: Maybe<{
    callbacks: CallbackMetadata[];
    step: StepMetadata;
  }>;
  export let step: JourneyStep;

  let alertNeedsFocus = false;
  let formMessageKey = '';
  let linkWrapper: HTMLElement;

  function determineSubmission() {
    // TODO: the below is more strict; all self-submitting cbs have to complete before submitting
    // if (stepMetadata.isStepSelfSubmittable && isStepReadyToSubmit(callbackMetadataArray)) {

    // The below variation is more liberal first self-submittable cb to call this wins.
    if (metadata?.step?.derived.isStepSelfSubmittable()) {
      form?.submit();
    }
  }

  afterUpdate(() => {
    alertNeedsFocus = !!form?.message;
  });

  onMount(() => {
    if (componentStyle === 'modal') {
      captureLinks(linkWrapper, journey);
    }
  });

  $: {
    formMessageKey = convertStringToKey(form?.message);
  }
</script>

<Form bind:formEl ariaDescribedBy="formFailureMessageAlert" onSubmitWhenValid={form?.submit}>
  {#if $styleStore?.logo && componentStyle !== 'modal'}
    <div class="tw_flex tw_justify-center tw_pb-4">
      <div
        class="tw_dialog-logo dark:tw_dialog-logo_dark"
        style={`--logo-light: ${encodeCssUrl(
          $styleStore.logo.light ?? '',
        )}; --logo-dark: ${encodeCssUrl($styleStore.logo.dark ?? '')}; height: ${
          $styleStore.logo.height ? `${$styleStore.logo.height}px` : '72px'
        }; width: ${$styleStore.logo.width ? `${$styleStore.logo.width}px` : '200px'};`}
      ></div>
    </div>
  {:else if componentStyle !== 'inline' && form?.icon}
    <div class="tw_flex tw_justify-center">
      <KeyIcon classes="tw_text-gray-400 tw_fill-current" size="72px" />
    </div>
  {/if}
  {#if componentStyle !== 'inline'}
    <h1 class="tw_primary-header dark:tw_primary-header_dark">
      <T key="loginHeader" />
    </h1>
  {/if}

  {#if form?.message}
    <Alert id="formFailureMessageAlert" needsFocus={alertNeedsFocus} type="error">
      {interpolate(formMessageKey, null, form?.message)}
    </Alert>
  {/if}

  {#each step?.callbacks as callback, idx}
    <CallbackMapper
      props={{
        callback,
        callbackMetadata: metadata?.callbacks[idx],
        selfSubmitFunction: determineSubmission,
        stepMetadata: metadata?.step && { ...metadata.step },
        style: $styleStore,
      }}
    />
  {/each}

  {#if metadata?.step?.derived.isUserInputOptional || !metadata?.step?.derived.isStepSelfSubmittable()}
    <Button busy={journey?.loading} style="primary" type="submit" width="full">
      <T key="loginButton" />
    </Button>
  {/if}

  {#if componentStyle !== 'inline'}
    <p class=" tw_my-4 tw_text-base tw_text-center tw_text-link-dark dark:tw_text-link-light">
      <button
        on:click|preventDefault={() => {
          journey.push({ journey: 'ResetPassword' });
        }}
      >
        {interpolate('forgotPassword', null, 'Forgot Password?')}
      </button>
      &nbsp;
      <button
        on:click|preventDefault={() => {
          journey.push({ journey: 'ForgottenUsername' });
        }}
      >
        {interpolate('forgotUsername', null, 'Forgot Username?')}
      </button>
    </p>

    <hr class="tw_border-0 tw_border-b tw_border-secondary-light dark:tw_border-secondary-dark" />

    <p
      bind:this={linkWrapper}
      class="tw_text-base tw_text-center tw_py-4 tw_text-secondary-dark dark:tw_text-secondary-light"
    >
      <T key="dontHaveAnAccount" html={true} />
    </p>
  {/if}
</Form>
